import { useState } from "react";
import { createPortal } from "react-dom";
import { Avatar, Stars, IC } from "../shared";

// ── Star filter pill ─────────────────────────────────────────────────────────
const starIcon = <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;

// ── Breakdown bar row ────────────────────────────────────────────────────────
const BreakdownRow = ({ star, count, max }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, width: 14, textAlign: "right" }}>{star}</div>
    <div style={{ flex: 1, height: 6, background: "var(--warm)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", borderRadius: 3, background: "#f5a623", width: max > 0 ? `${(count / max) * 100}%` : "0%", transition: "width .4s ease" }}/>
    </div>
    <div style={{ fontSize: 10, color: "var(--muted)", width: 18 }}>{count}</div>
  </div>
);

// ── Role pill ────────────────────────────────────────────────────────────────
const RolePill = ({ role }) => {
  if (!role) return null;
  return (
    <span style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: role === "driver" ? "rgba(43,91,224,.08)" : "rgba(15,158,104,.08)", color: role === "driver" ? "var(--accent)" : "var(--success)", textTransform: "uppercase", letterSpacing: "0.03em", marginLeft: 6 }}>
      As {role}
    </span>
  );
};

// ── Review card ──────────────────────────────────────────────────────────────
const ReviewCard = ({ r, cardStyle }) => (
  <div style={cardStyle}>
    <div style={{ display: "flex", gap: 12 }}>
      <Avatar user={{ id: r.raterId, name: r.raterName, avatar: r.raterAvatar }} size={38}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{r.raterName}</div>
          <div style={{ fontSize: 11, color: "var(--muted)" }}>
            {r.ratedAt?.toDate
              ? r.ratedAt.toDate().toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })
              : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Stars rating={r.score} size={12}/>
          <RolePill role={r.ratedAs}/>
        </div>
        {r.comment && (
          <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.4, marginTop: 4 }}>{r.comment}</div>
        )}
      </div>
    </div>
  </div>
);

// ── All Reviews Full Screen ──────────────────────────────────────────────────
const AllReviews = ({ reviews, avgRating, reviewCount, userName, onClose }) => {
  const [starFilter, setStarFilter] = useState(0); // 0 = all

  // Count per star
  const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach(r => { if (counts[r.score] !== undefined) counts[r.score]++; });
  const maxCount = Math.max(...Object.values(counts), 1);

  const filtered = starFilter === 0 ? reviews : reviews.filter(r => r.score === starFilter);

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9500, background: "var(--paper)", display: "flex", flexDirection: "column" }} className="aslide">
      {/* Header */}
      <div style={{ paddingTop: "env(safe-area-inset-top, 0px)", background: "var(--paper)", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px" }}>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: 12, background: "var(--warm)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <span style={{ width: 18, height: 18, color: "var(--ink)" }}>{IC.back}</span>
          </button>
          <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--ink)", flex: 1 }}>All Reviews</div>
          <span style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--accent)", background: "rgba(43,91,224,.08)", padding: "3px 10px", borderRadius: 99, border: "1px solid rgba(43,91,224,.15)" }}>{reviewCount}</span>
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {/* Summary with breakdown */}
        <div style={{ padding: "16px 16px 0" }}>
          <div style={{ display: "flex", gap: 20, alignItems: "center", padding: 16, background: "var(--card)", borderRadius: 18, border: "1.5px solid var(--border)", marginBottom: 14 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 32, color: "var(--accent)" }}>{avgRating ? avgRating.toFixed(1) : "—"}</div>
              <Stars rating={avgRating} size={14}/>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{reviewCount} review{reviewCount !== 1 ? "s" : ""}</div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              {[5, 4, 3, 2, 1].map(s => (
                <BreakdownRow key={s} star={s} count={counts[s]} max={maxCount}/>
              ))}
            </div>
          </div>
        </div>

        {/* Star filter pills */}
        <div style={{ display: "flex", gap: 6, padding: "0 16px 12px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {[{ label: "All", val: 0 }, ...([5,4,3,2,1].map(n => ({ label: n, val: n })))].map(f => {
            const active = starFilter === f.val;
            return (
              <button key={f.val} onClick={() => setStarFilter(f.val)} style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "7px 14px", borderRadius: 99, flexShrink: 0,
                border: active ? "1.5px solid var(--accent)" : "1.5px solid var(--border)",
                background: active ? "var(--accent)" : "var(--card)",
                fontFamily: "Syne", fontWeight: 700, fontSize: 12,
                color: active ? "#fff" : "var(--muted)",
                cursor: "pointer", transition: "all .15s",
              }}>
                {f.val > 0 && <span style={{ width: 12, height: 12, color: active ? "#fff" : "#f5a623" }}>{starIcon}</span>}
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Filter result count */}
        <div style={{ padding: "0 16px 8px", fontSize: 12, color: "var(--muted)", fontFamily: "Syne", fontWeight: 600 }}>
          {starFilter === 0 ? `Showing all ${reviews.length} reviews` : `Showing ${filtered.length} review${filtered.length !== 1 ? "s" : ""} with ${starFilter} star${starFilter !== 1 ? "s" : ""}`}
        </div>

        {/* Review list */}
        <div style={{ padding: "0 16px 40px" }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>
                <span style={{ width: 36, height: 36, color: "#ddd", display: "inline-block" }}>{starIcon}</span>
              </div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15 }}>No {starFilter}-star reviews</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{userName || "This user"} hasn't received any {starFilter}-star ratings yet</div>
            </div>
          ) : (
            filtered.map(r => (
              <ReviewCard key={r.id} r={r} cardStyle={{
                background: "var(--card)", borderRadius: 16, padding: "14px 16px",
                border: "1.5px solid var(--border)", marginBottom: 10,
              }}/>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export { AllReviews, ReviewCard, RolePill, BreakdownRow };
export default AllReviews;
